import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Botao, Chip, Modal } from '../../ui';
import MiniaturaFotoVistoria from '../vistoria/MiniaturaFotoVistoria';
import { InspectionCaptureProps } from './types';
import MarcarAvariaCanvas from './MarcarAvariaCanvas';
import { useInspectionCapture } from './useInspectionCapture';
import { RootState } from '../../store/store';

/**
 * Captura das fotos da vistoria — o invólucro (reestilizado na at-63, D2).
 *
 * 🔴 **O MOTOR NÃO FOI TOCADO.** Todo o comportamento continua em
 * `useInspectionCapture` (`getUserMedia`, as esperas com timeout pelo `<video>`,
 * a legenda **queimada** na foto, `toBlob` JPEG 0.9, a geolocalização com timeout
 * de 8s e o **modelo de slots** do bug B6 da at-24) e em `utils.ts`/`types.ts`.
 * O que mudou nesta branch: os botões viraram `Botao`, a grade virou
 * `MiniaturaFotoVistoria` e os dois overlays de diálogo viraram o `Modal` do kit.
 *
 * 🔴 **A CÂMERA VIVE NUM `Modal` DO KIT** (pedido de Victor, 10/09/2026: *"coloque
 * a visualização de câmera dentro de um modal, que nem no modal de fotos de
 * Documentos. Tudo deve ser padronizado"*). Isso **substitui** o desenho anterior,
 * em que ela era um bloco inline com um botão "Tela cheia" que abria um overlay
 * `fixed inset-0 z-[9999]` — **os dois saíram**. Agora os **três** diálogos deste
 * componente (câmera, prévia da foto e editor de avaria) são o mesmo `Modal`, com
 * o mesmo cabeçalho e as ações no `rodape`.
 *
 * ⚠️ **O que é próprio da vistoria foi preservado dentro do modal**, e não existe
 * na `CapturaDocumentos`: a **legenda do slot atual** e o **contador "N de M"**
 * sobre o vídeo (é o que diz ao consultor qual foto tirar), o "Carregando
 * câmera..." do `isCameraReady`, o "Salvando..." e a captura **sequencial por
 * slot**.
 *
 * 📌 **Não foi preciso o `setTimeout` da `CapturaDocumentos`** para ligar o stream
 * ao `<video>`: aqui o `Modal` abre por `aberto={isCameraActive}`, então o
 * elemento monta **no mesmo commit** em que `startCamera` liga aquela flag, e o
 * efeito do hook (que já espera por `videoRef.current`) o encontra. Nada no motor
 * mudou.
 *
 * ⚠️ **Saiu o `useEffect` que travava `document.body.style.overflow`.** Era
 * inócuo neste app — o `body` já é `overflow: hidden` (`tokens/base.css`) e quem
 * rola é o `<main>` do shell. O `Modal` do kit também não trava rolagem, de
 * propósito.
 *
 * ⚠️ **Saiu o listener de `Escape` da prévia**: o `Modal` do kit já fecha por Esc
 * e por clique fora, e ainda prende o foco — que o overlay manual não fazia.
 */

/** O item que o motor devolve. Derivado do hook para não duplicar o tipo. */
type ItemCapturado = ReturnType<typeof useInspectionCapture>['images'][number];

const InspectionCapture: React.FC<InspectionCaptureProps> = ({
  captions,
  maxPhotos,
  onImagesChange,
  onImagesWithFileChange,
  showFilePicker = true,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  /** Dispara o seletor nativo, que fica escondido — ver o bloco do botão. */
  const seletorArquivos = useRef<HTMLInputElement>(null);
  const [arquivosEscolhidos, setArquivosEscolhidos] = useState(0);
  const [previewImage, setPreviewImage] = useState<ItemCapturado | null>(null);
  /** Foto em edição no canvas de marcação de avaria (at-24). */
  const [marcandoAvaria, setMarcandoAvaria] = useState<ItemCapturado | null>(null);

  /**
   * ⚠️ Era `state.auth.isAdmin` — flag **legada** que nenhuma implementação nova
   * deve ler (`01-identity.md`: *"ao tocar em código que consulte `isAdmin`,
   * migrar a checagem para `userType`"*). O critério equivalente no frontend é o
   * do `PendenciasBanner`: `userType` em TI/ADMIN. Para TI/ADMIN o resultado
   * visível é o mesmo de antes; muda só a fonte da checagem.
   *
   * 📌 A checagem é dupla de propósito (aqui **e** na tela, que passa a prop) —
   * é a estrutura que já existia, e o `showFilePicker` segue sendo prop porque
   * um dia o painel de TI pode liberar o seletor para outro papel.
   */
  const userType = useSelector((state: RootState) => state.auth.userType);
  const elevado = userType === 'TI' || userType === 'ADMIN';

  const {
    videoRef,
    images,
    currentIndex,
    isCameraActive,
    isCameraReady,
    error,
    startCamera,
    stopCamera,
    captureFromCamera,
    handleFileInput,
    removeImage,
    aplicarMarcacaoAvaria,
    removerMarcacaoAvaria,
  } = useInspectionCapture({ captions, maxPhotos, onImagesChange, onImagesWithFileChange });

  const hasConfigError = captions.length !== maxPhotos;
  const currentCaption = captions[currentIndex];
  const totalCaption = captions.length;
  const currentCount = images.length;

  const handleCapture = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await captureFromCamera();
    } finally {
      setIsSaving(false);
    }
  };

  const closePreview = () => setPreviewImage(null);

  const capturaIndisponivel =
    isSaving || !isCameraReady || currentIndex >= maxPhotos || hasConfigError;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {hasConfigError && (
        <p className="tw-m-0 tw-text-xs tw-font-semibold tw-text-danger">
          Configuração inválida: a lista de legendas e o número máximo de fotos precisam
          ter o mesmo tamanho.
        </p>
      )}

      <div className="tw-flex tw-flex-col tw-gap-3">
        <Botao
          variante="primario"
          tamanho="lg"
          larguraTotal
          onClick={startCamera}
          disabled={hasConfigError || currentIndex >= maxPhotos}
        >
          {currentIndex >= maxPhotos
            ? 'Todas as fotos foram capturadas'
            : `Abrir câmera (${currentCount} de ${totalCaption})`}
        </Botao>

        {/* A próxima legenda também FORA do modal: é o que o consultor lê antes
            de abrir a câmera, para saber o que vai enquadrar. */}
        {currentCaption && (
          <p className="tw-m-0 tw-rounded-lg tw-border-l-4 tw-border-brand tw-bg-superficie tw-px-3 tw-py-2 tw-text-xs tw-text-texto-corpo">
            <span className="tw-font-bold">Próxima foto:</span> {currentCaption}
          </p>
        )}

        {/*
          Seletor de arquivos (TI/ADMIN + prop da tela — ver o aviso acima).

          🔴 O `<input type="file">` era renderizado **cru**, com o widget nativo
          do navegador (Victor, 10/09/2026: *"não pode ficar puro desse jeito, sem
          CSS"*). O input continua sendo o mecanismo — é o único jeito de abrir o
          seletor —, mas fica **escondido** e é disparado pelo `Botao` do kit.
        */}
        {elevado && showFilePicker && (
          <div className="tw-flex tw-flex-col tw-gap-1">
            <Botao
              variante="secundario"
              iconeEsquerda="mais"
              larguraTotal
              disabled={hasConfigError || currentIndex >= maxPhotos}
              onClick={() => seletorArquivos.current?.click()}
            >
              Escolher arquivos do aparelho
            </Botao>

            <input
              ref={seletorArquivos}
              type="file"
              accept="image/*"
              multiple
              className="tw-hidden"
              onChange={(e) => {
                setArquivosEscolhidos(e.target.files?.length ?? 0);
                handleFileInput(e.target.files);
                /* Zera o valor para escolher o MESMO arquivo de novo disparar
                   `onChange` (o navegador não reemite para valor idêntico) —
                   caso real depois de excluir uma foto e recapturá-la. */
                e.target.value = '';
              }}
            />

            {arquivosEscolhidos > 0 && (
              <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
                {arquivosEscolhidos === 1
                  ? '1 arquivo escolhido.'
                  : `${arquivosEscolhidos} arquivos escolhidos.`}{' '}
                Confira as miniaturas abaixo.
              </p>
            )}
          </div>
        )}

        {error && (
          <p className="tw-m-0 tw-text-xs tw-font-semibold tw-text-danger" role="alert">
            {error}
          </p>
        )}
      </div>

      {/*
        🔴 A CÂMERA, no `Modal` do kit — o mesmo desenho da `CapturaDocumentos`
        (at-60), como Victor pediu. `aberto={isCameraActive}` é o que faz o
        `<video>` montar no mesmo commit em que `startCamera` liga a flag, sem
        precisar do `setTimeout` daquela tela (ver o aviso do cabeçalho).

        ⚠️ `ocupado={isSaving}`: fechar no meio de uma captura deixaria o
        consultor sem saber se a foto entrou.
      */}
      <Modal
        aberto={isCameraActive}
        titulo="Capturar foto"
        descricao={
          currentCaption
            ? `${currentCaption} — foto ${currentCount + 1} de ${totalCaption}.`
            : 'Todas as fotos deste veículo já foram capturadas.'
        }
        tamanho="lg"
        ocupado={isSaving}
        aoFechar={stopCamera}
        rodape={
          <>
            <Botao
              variante="primario"
              tamanho="lg"
              onClick={() => void handleCapture()}
              disabled={capturaIndisponivel}
            >
              {isSaving ? 'Salvando...' : `Tirar foto (${currentCount} de ${totalCaption})`}
            </Botao>
            <Botao variante="secundario" disabled={isSaving} onClick={stopCamera}>
              Fechar câmera
            </Botao>
          </>
        }
      >
        <div className="tw-relative">
          <video
            ref={videoRef}
            className="tw-aspect-video tw-h-auto tw-w-full tw-rounded-xl tw-bg-black tw-object-cover"
            playsInline
            muted
            autoPlay
          />

          {!isCameraReady && (
            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-black/35 tw-font-bold tw-text-white">
              Carregando câmera...
            </div>
          )}

          {isSaving && (
            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-black/55 tw-font-bold tw-text-white">
              Salvando...
            </div>
          )}

          {/* A legenda do slot corrente SOBRE o vídeo: é ela que será QUEIMADA
              na foto (at-24), então precisa estar visível na hora de enquadrar —
              e é o que a câmera de Documentos não tem. */}
          {currentCaption && (
            <div className="tw-absolute tw-left-2.5 tw-right-2.5 tw-top-2.5 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-bg-black/70 tw-p-2.5">
              <span className="tw-flex-1 tw-break-words tw-text-sm tw-font-semibold tw-text-white">
                {currentCaption}
              </span>
              <span className="tw-shrink-0 tw-rounded tw-bg-white/20 tw-px-2 tw-py-1 tw-text-xs tw-text-white">
                {currentCount} / {totalCaption}
              </span>
            </div>
          )}
        </div>
      </Modal>

      {images.length > 0 && (
        <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3 md:tw-grid-cols-4">
          {images.map((item) => (
            <MiniaturaFotoVistoria
              key={item.id}
              src={item.previewUrl}
              legenda={item.meta.caption}
              avaria={item.meta.damaged}
              coordenadas={{
                latitude: item.meta.latitude,
                longitude: item.meta.longitude,
              }}
              acoes={
                <>
                  <Botao
                    variante="secundario"
                    tamanho="sm"
                    larguraTotal
                    onClick={() => setPreviewImage(item)}
                  >
                    Visualizar
                  </Botao>
                  {/* A marcação é desenhada sobre a foto e queimada na imagem (at-24) —
                      ao editar, o canvas parte SEMPRE da foto original, para o traço
                      não acumular por cima de traço anterior. */}
                  <Botao
                    variante="secundario"
                    tamanho="sm"
                    larguraTotal
                    onClick={() => setMarcandoAvaria(item)}
                  >
                    {item.meta.damaged ? 'Editar marcação' : 'Marcar Avaria'}
                  </Botao>
                  {item.meta.damaged && (
                    <Botao
                      variante="secundario"
                      tamanho="sm"
                      larguraTotal
                      onClick={() => removerMarcacaoAvaria(item.id)}
                    >
                      Remover Avaria
                    </Botao>
                  )}
                  <Botao
                    variante="perigo"
                    tamanho="sm"
                    larguraTotal
                    onClick={() => {
                      if (previewImage?.id === item.id) setPreviewImage(null);
                      removeImage(item.id);
                    }}
                  >
                    Excluir
                  </Botao>
                </>
              }
            />
          ))}
        </div>
      )}

      {marcandoAvaria && (
        <MarcarAvariaCanvas
          // Base é SEMPRE a foto original: reeditar não risca sobre o traço antigo.
          imagemUrl={marcandoAvaria.previewUrlOriginal}
          legenda={marcandoAvaria.meta.caption}
          onCancelar={() => setMarcandoAvaria(null)}
          onConfirmar={(arquivo) => {
            aplicarMarcacaoAvaria(marcandoAvaria.id, arquivo);
            setMarcandoAvaria(null);
          }}
        />
      )}

      <Modal
        aberto={previewImage !== null}
        titulo={previewImage?.meta.caption || 'Foto da vistoria'}
        descricao="A legenda já está gravada na imagem."
        tamanho="lg"
        aoFechar={closePreview}
        rodape={
          <Botao variante="secundario" onClick={closePreview}>
            Fechar
          </Botao>
        }
      >
        {previewImage && (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <img
              src={previewImage.previewUrl}
              alt={previewImage.meta.caption}
              className="tw-max-h-[60vh] tw-w-full tw-rounded-xl tw-bg-black tw-object-contain"
            />
            {previewImage.meta.damaged && <Chip variante="perigo">Avaria marcada</Chip>}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InspectionCapture;
